/**
 * Owner action menu — pencil → action sheet → delete confirm.
 *
 * SCRUM-49: lifts the «edit / delete / cancel series» flow out of every
 * owner-facing card so a single component handles it. Used by
 * LiveAnnouncementHero, LiveDetailScreen, and (gradually) the other
 * owner surfaces listed in the ticket spec.
 *
 * The component is purely presentational — it doesn't talk to the API
 * itself, the caller passes `onDeleteOne` / `onDeleteSeries` handlers
 * and decides whether to call `api.deleteLive(Series)` or
 * `api.deleteRoom(Series)`. That keeps the live and room paths
 * symmetrical and lets callers attach their own toast / navigation /
 * analytics on success.
 */
import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Platform, Pressable, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from './ui';
import { useTheme } from '../context/ThemeContext';
import { fonts } from '../styles/typography';

import { ACCENT_TEXT } from '../styles/accentText';
export interface OwnerCardMenuProps {
  visible: boolean;
  onClose: () => void;
  /**
   * True when this event belongs to a regular weekly series (Live with
   * `isRegular || parentLiveId`, Room with `isRecurring || parentRoomId`).
   * Series members get the two-option delete modal; one-offs get a plain
   * confirm.
   */
  isPartOfSeries: boolean;
  /**
   * Title shown in the «Delete?» confirm copy. Falls back to a generic
   * «событие» when null/empty so the modal stays readable for rooms with
   * no title set yet.
   */
  eventTitle?: string | null;
  /**
   * CORE-879 — кому уйдёт весть об удалении, вторая фраза подтверждения.
   * Круг извещают не у всякого события: у Live-сессии программы пишут
   * купившим, у приватной — записавшимся. Вызывающий знает уровень
   * видимости и передаёт фразу; без неё — прежняя, про Круг.
   */
  deleteNotice?: string;
  /** Resolves the edit action. Called BEFORE the menu closes so callers can race-open their modal. */
  onEdit: () => void;
  /** Removes only the clicked iteration. May be async; menu closes after resolve. */
  onDeleteOne: () => Promise<void> | void;
  /**
   * Removes the clicked iteration + every future one in the series. Only
   * surfaced when `isPartOfSeries` is true. May be async.
   */
  onDeleteSeries?: () => Promise<void> | void;
}

type Step = 'menu' | 'confirm-one' | 'confirm-series';

export function OwnerCardMenu({
  visible,
  onClose,
  isPartOfSeries,
  eventTitle,
  deleteNotice = 'Подписчики увидят уведомление в чате Круга.',
  onEdit,
  onDeleteOne,
  onDeleteSeries,
}: OwnerCardMenuProps) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { colors: themeColors, isDark } = useTheme();
  const [step, setStep] = useState<Step>('menu');
  const [busy, setBusy] = useState(false);

  // Reset to the menu step whenever the parent re-opens us — otherwise a
  // previously-cancelled confirm step would still be the active view.
  useEffect(() => {
    if (visible) setStep('menu');
  }, [visible]);

  if (!visible) return null;

  const titleLabel = eventTitle?.trim() ? `«${eventTitle.trim()}»` : 'событие';

  const handleDeletePress = () => {
    setStep(isPartOfSeries ? 'confirm-series' : 'confirm-one');
  };

  const runHandler = async (fn?: () => Promise<void> | void) => {
    if (!fn || busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  const renderMenu = () => (
    <View style={styles.menuRows}>
      <Pressable
        testID="owner-menu-edit"
        accessibilityRole="button"
        accessibilityLabel="Редактировать"
        style={({ pressed }) => [
          styles.menuRow,
          { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
          pressed && { opacity: 0.6 },
        ]}
        onPress={() => { onEdit(); onClose(); }}
      >
        <Ionicons name="create-outline" size={20} color={themeColors.textPrimary} />
        <Typography variant="body" style={[styles.menuRowText, { color: themeColors.textPrimary }]}>
          Редактировать
        </Typography>
      </Pressable>
      <Pressable
        testID="owner-menu-delete"
        accessibilityRole="button"
        accessibilityLabel="Удалить"
        style={({ pressed }) => [
          styles.menuRow,
          { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
          pressed && { opacity: 0.6 },
        ]}
        onPress={handleDeletePress}
      >
        <Ionicons name="trash-outline" size={20} color="#EF4444" />
        <Typography variant="body" style={[styles.menuRowText, { color: '#EF4444' }]}>
          Удалить
        </Typography>
      </Pressable>
    </View>
  );

  const renderConfirmOne = () => (
    <>
      <View style={[styles.iconCircle, { backgroundColor: 'rgba(239,68,68,0.10)' }]}>
        <Ionicons name="warning-outline" size={26} color="#EF4444" />
      </View>
      <Typography variant="h3" style={[styles.heading, { color: themeColors.textPrimary }]}>
        Удалить {titleLabel}?
      </Typography>
      <Typography variant="body" style={[styles.body, { color: themeColors.textPrimary }]}>
        Действие необратимо. {deleteNotice}
      </Typography>
      <View style={styles.buttons}>
        <Button
          testID="owner-menu-delete-confirm"
          variant="secondary"
          onPress={() => runHandler(onDeleteOne)}
          disabled={busy}
          style={[
            styles.btn,
            { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          ]}
        >
          {busy ? '...' : 'Удалить'}
        </Button>
        <Button
          testID="owner-menu-delete-cancel"
          variant="secondary"
          onPress={onClose}
          disabled={busy}
          style={[styles.btn, isDark && { borderColor: 'rgba(255,255,255,0.15)' }]}
        >
          Не сейчас
        </Button>
      </View>
    </>
  );

  const renderConfirmSeries = () => (
    <>
      <View style={[styles.iconCircle, { backgroundColor: 'rgba(233, 130, 69,0.10)' }]}>
        <Ionicons name="calendar-outline" size={26} color={ACCENT_TEXT} />
      </View>
      <Typography variant="h3" style={[styles.heading, { color: themeColors.textPrimary }]}>
        Это регулярное событие
      </Typography>
      <Typography variant="body" style={[styles.body, { color: themeColors.textPrimary }]}>
        Прошлые встречи сохранятся.
      </Typography>
      <View style={styles.buttons}>
        <Button
          variant="secondary"
          onPress={() => runHandler(onDeleteOne)}
          disabled={busy}
          style={[styles.btn, isDark && { borderColor: 'rgba(255,255,255,0.15)' }]}
        >
          Отменить только это
        </Button>
        <Button
          variant="secondary"
          onPress={() => runHandler(onDeleteSeries)}
          disabled={busy || !onDeleteSeries}
          style={[
            styles.btn,
            { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          ]}
        >
          {busy ? '...' : 'Отменить эту и все будущие'}
        </Button>
        <Button
          variant="secondary"
          onPress={onClose}
          disabled={busy}
          style={[styles.btn, isDark && { borderColor: 'rgba(255,255,255,0.15)' }]}
        >
          Отмена
        </Button>
      </View>
    </>
  );

  return (
    <Pressable style={styles.backdrop} onPress={busy ? undefined : onClose}>
      <Pressable
        style={[
          styles.card,
          isDesktop && styles.cardDesktop,
          { backgroundColor: themeColors.surfaceHover },
        ]}
        onPress={(e) => e.stopPropagation()}
      >
        {step === 'menu' && renderMenu()}
        {step === 'confirm-one' && renderConfirmOne()}
        {step === 'confirm-series' && renderConfirmSeries()}
      </Pressable>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 6000,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(4px)',
    }),
  } as any,
  card: {
    borderRadius: 32,
    paddingHorizontal: 32,
    paddingTop: 32,
    paddingBottom: 24,
    width: '90%',
    maxWidth: 420,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
    }),
  } as any,
  cardDesktop: {
    paddingHorizontal: 48,
    paddingTop: 48,
    paddingBottom: 32,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  heading: {
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: fonts.heading,
    fontWeight: 'normal',
    fontSize: 24,
    letterSpacing: -0.5,
  },
  body: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 24,
    lineHeight: 22,
    fontSize: 15,
    maxWidth: 320,
    fontFamily: fonts.text,
    fontWeight: 'normal',
  },
  buttons: {
    width: '100%',
    gap: 10,
  },
  btn: {
    width: '100%',
    height: 48,
    borderRadius: 14,
  },
  menuRows: {
    width: '100%',
    gap: 4,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    gap: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  menuRowText: {
    fontSize: 16,
    fontFamily: fonts.text,
  },
});
